import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ShareIcon from '@mui/icons-material/Share';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Oth360Banner } from 'src/components/oth/Oth360Banner';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import s from './Oth360FormPage.module.scss';
import {
  getInviteModeFlags,
  loadOth360InviteContext,
  saveOth360InviteContext,
} from './oth360InviteStorage';
import type {
  Oth360DevelopmentPrompts,
  Oth360RatingQuestion,
} from './standardOth360Form';

type TabId = 'status' | 'utvikling';

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

async function shareOrCopy(url: string, title: string) {
  try {
    if (navigator.share) {
      await navigator.share({ title, url });
      return { ok: true, mode: 'share' as const };
    }
    await navigator.clipboard.writeText(url);
    return { ok: true, mode: 'copy' as const };
  } catch {
    return { ok: false, mode: 'error' as const };
  }
}

/**
 * Best-effort server schema support.
 * If schemaJson matches our "standard" schema structure, render using it.
 * Otherwise, fall back to the standard form (so users are never blocked).
 */
function getEffectiveSchema(input: {
  standard: {
    ratingQuestions: Oth360RatingQuestion[];
    prompts: Oth360DevelopmentPrompts;
  };
  schemaJson: unknown;
}): {
  ratingQuestions: Oth360RatingQuestion[];
  prompts: Oth360DevelopmentPrompts;
  source: 'server' | 'standard';
} {
  const standard = { ...input.standard, source: 'standard' as const };

  if (!isRecord(input.schemaJson)) {
    return standard;
  }

  // Minimal schema shape we can understand today (optional future-proofing).
  // {
  //   ratingQuestions: Array<{ id, category, text }>
  //   prompts: { continueWith, stopWith, startWith }
  // }
  const ratingQuestionsRaw = input.schemaJson.ratingQuestions;
  const promptsRaw = input.schemaJson.prompts;

  if (!Array.isArray(ratingQuestionsRaw) || !isRecord(promptsRaw)) {
    return standard;
  }

  const ratingQuestions = ratingQuestionsRaw
    .filter(isRecord)
    .map((q) => ({
      id: typeof q.id === 'string' ? q.id : '',
      category: typeof q.category === 'string' ? q.category : '',
      text: typeof q.text === 'string' ? q.text : '',
    }))
    .filter((q) => q.id && q.category && q.text);

  const prompts = {
    continueWith:
      typeof promptsRaw.continueWith === 'string'
        ? promptsRaw.continueWith
        : standard.prompts.continueWith,
    stopWith:
      typeof promptsRaw.stopWith === 'string'
        ? promptsRaw.stopWith
        : standard.prompts.stopWith,
    startWith:
      typeof promptsRaw.startWith === 'string'
        ? promptsRaw.startWith
        : standard.prompts.startWith,
  };

  if (ratingQuestions.length === 0) {
    return standard;
  }

  return { ratingQuestions, prompts, source: 'server' };
}

function RatingScale(props: {
  value: number | null;
  onChange: (value: number) => void;
  ariaLabel: string;
  labels: string[];
  disabled?: boolean;
}) {
  return (
    <div className={s.scale} role="radiogroup" aria-label={props.ariaLabel}>
      {props.labels.map((label, i) => {
        const n = i + 1;
        const selected = props.value === n;

        return (
          <div key={n} className={s.scaleItemContainer}>
            <button
              type="button"
              className={clsx(s.scaleButton, selected && s.scaleButtonSelected)}
              role="radio"
              aria-checked={selected}
              aria-label={`${n} = ${label}`}
              disabled={props.disabled}
              onClick={() => props.onChange(n)}
            >
              <span className={s.scaleNumber}>{n}</span>
            </button>
            <p className={s.scaleLabel}>{label}</p>
          </div>
        );
      })}
    </div>
  );
}

export const Oth360FormPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ctx = useMemo(() => loadOth360InviteContext(), []);

  const [tab, setTab] = useState<TabId>('status');
  const [ratings, setRatings] = useState<Record<string, number | null>>({});
  const [devText, setDevText] = useState({
    continueWith: '',
    stopWith: '',
    startWith: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(
    ctx?.hasSubmitted ?? false
  );
  const [showSuccess, setShowSuccess] = useState(false);

  const onShareEmployeeLink = async () => {
    const link = ctx?.employeeLink;
    if (!link) {
      toast.error(t('oth360Form.share.employeeLinkMissing'));
      return;
    }
    const r = await shareOrCopy(link, t('oth360Form.share.title'));
    if (!r.ok) {
      toast.error(t('oth360Form.share.error'));
    } else {
      toast.success(
        r.mode === 'copy'
          ? t('oth360Form.share.copied')
          : t('oth360Form.share.shared')
      );
    }
  };

  if (!ctx) {
    return (
      <div className={s.page}>
        <div className={s.content}>
          <p>{t('oth360Form.missingContext.message')}</p>
          <div className={s.actions}></div>
        </div>
      </div>
    );
  }

  const subjectName = ctx.subjectName || t('oth360Form.defaultName');
  const scaleLabels = [
    t('oth360Form.scale.1'),
    t('oth360Form.scale.2'),
    t('oth360Form.scale.3'),
    t('oth360Form.scale.4'),
    t('oth360Form.scale.5'),
    t('oth360Form.scale.6'),
  ];
  const standardSchema = {
    ratingQuestions: [
      {
        id: 'strategy_direction',
        category: t('oth360Form.questions.strategy_direction.category'),
        text: t('oth360Form.questions.strategy_direction.text', {
          name: subjectName,
        }),
      },
      {
        id: 'organizing_roles',
        category: t('oth360Form.questions.organizing_roles.category'),
        text: t('oth360Form.questions.organizing_roles.text', {
          name: subjectName,
        }),
      },
      {
        id: 'culture_values',
        category: t('oth360Form.questions.culture_values.category'),
        text: t('oth360Form.questions.culture_values.text', {
          name: subjectName,
        }),
      },
      {
        id: 'execution_power',
        category: t('oth360Form.questions.execution_power.category'),
        text: t('oth360Form.questions.execution_power.text', {
          name: subjectName,
        }),
      },
      {
        id: 'psychological_safety',
        category: t('oth360Form.questions.psychological_safety.category'),
        text: t('oth360Form.questions.psychological_safety.text', {
          name: subjectName,
        }),
      },
      {
        id: 'development_feedback',
        category: t('oth360Form.questions.development_feedback.category'),
        text: t('oth360Form.questions.development_feedback.text', {
          name: subjectName,
        }),
      },
      {
        id: 'trust',
        category: t('oth360Form.questions.trust.category'),
        text: t('oth360Form.questions.trust.text', { name: subjectName }),
      },
    ],
    prompts: {
      continueWith: t('oth360Form.prompts.continueWith', { name: subjectName }),
      stopWith: t('oth360Form.prompts.stopWith', { name: subjectName }),
      startWith: t('oth360Form.prompts.startWith', { name: subjectName }),
    },
  };

  const schema = getEffectiveSchema({
    standard: standardSchema,
    schemaJson: ctx.form?.schemaJson,
  });

  const { isSelfLink } = getInviteModeFlags(ctx.mode);
  const requiredRatingIds = schema.ratingQuestions.map((q) => q.id);
  const isStatusComplete = requiredRatingIds.every(
    (id) => typeof ratings[id] === 'number'
  );
  const isDevelopmentComplete =
    devText.continueWith.trim().length > 0 &&
    devText.stopWith.trim().length > 0 &&
    devText.startWith.trim().length > 0;
  const isFormComplete = isStatusComplete && isDevelopmentComplete;
  const canGoNext = isStatusComplete && !hasSubmitted && !isSubmitting;
  const canSubmit = isFormComplete && !hasSubmitted && !isSubmitting;

  const onResults = () => {
    navigate(PathRoutes.Oth360ResultsRoute);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (hasSubmitted) {
      toast(t('oth360Form.submit.alreadySubmitted'));
      return;
    }

    if (!isSelfLink && !isFormComplete) {
      toast.error(t('oth360Form.submit.fillAllBeforeSubmitting'));
      return;
    }

    const idempotencyKey =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
      setIsSubmitting(true);

      const url = new URL('/api/oth/360/responses', window.location.origin);
      url.searchParams.set('mode', ctx.mode);

      const res = await fetch(url.toString(), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          Accept: 'application/json',
        },
        body: JSON.stringify({
          idempotencyKey,
          answers: {
            ratings: Object.fromEntries(
              requiredRatingIds.map((id) => [id, ratings[id]])
            ),
            text: devText,
          },
        }),
      });

      if (!res.ok) {
        let message = t('oth360Form.submit.couldNotSubmit');
        try {
          const text = await res.text();
          if (text) {
            message = text;
          }
        } catch {
          // ignore
        }
        throw new Error(message);
      }

      const data = (await res.json()) as Partial<{
        result?: { responseId?: string; hasSubmitted?: boolean };
      }>;

      const nextHasSubmitted = data.result?.hasSubmitted === true;
      if (nextHasSubmitted) {
        const nextCtx = { ...ctx, hasSubmitted: true };
        saveOth360InviteContext(nextCtx);
        setHasSubmitted(true);
      }

      setShowSuccess(true);
      toast.success(t('oth360Form.submit.submitted'));
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : t('oth360Form.submit.couldNotSubmit');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess || hasSubmitted) {
    return (
      <div className={s.page}>
        <Oth360Banner
          title={t('oth360Form.title')}
          actions={
            isSelfLink && hasSubmitted ? (
              <>
                <button
                  type="button"
                  className={clsx(s.heroButton, s.resultsButton)}
                  onClick={onResults}
                >
                  {t('oth360Form.buttons.results')}
                </button>
                <button
                  type="button"
                  className={clsx(s.heroButton, s.shareButton)}
                  onClick={onShareEmployeeLink}
                  disabled={!ctx?.employeeLink}
                >
                  {t('oth360Form.share.button')}
                  <ShareIcon className={s.shareIcon} fontSize="small" />
                </button>
              </>
            ) : null
          }
        />

        <main className={s.content}>
          <div className={s.successCard}>
            <div className={s.successHeader}>
              <CheckCircleRoundedIcon className={s.successIcon} />
              <div>
                <h2 className={s.successTitle}>
                  {t('oth360Form.success.title')}
                </h2>
                <p className={s.successSubtitle}>
                  {t('oth360Form.success.subtitle')}
                </p>
              </div>
            </div>

            {isSelfLink && (
              <div className={s.heroActions}>
                <button
                  type="button"
                  className={clsx(s.primaryButton)}
                  onClick={onResults}
                >
                  {t('oth360Form.buttons.results')}
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <Oth360Banner title={t('oth360Form.title')} />

      <nav className={s.tabs} aria-label={t('oth360Form.tabs.ariaLabel')}>
        <div className={s.tabsInner}>
          <button
            type="button"
            className={clsx(s.tab, tab === 'status' && s.tabActive)}
            onClick={() => setTab('status')}
          >
            {t('oth360Form.tabs.status')}
          </button>
          <button
            type="button"
            className={clsx(s.tab, tab === 'utvikling' && s.tabActive)}
            onClick={() => setTab('utvikling')}
          >
            {t('oth360Form.tabs.development')}
          </button>
        </div>
      </nav>

      <main className={s.content}>
        <form onSubmit={onSubmit}>
          {tab === 'status' ? (
            <>
              {schema.ratingQuestions.map((q) => (
                <section key={q.id} className={s.questionBlock}>
                  <p className={s.category}>{q.category}</p>
                  <p className={s.questionText}>{q.text}</p>
                  <RatingScale
                    value={ratings[q.id] ?? null}
                    onChange={(value) =>
                      setRatings((prev) => ({ ...prev, [q.id]: value }))
                    }
                    ariaLabel={q.category}
                    labels={scaleLabels}
                    disabled={hasSubmitted || isSubmitting}
                  />
                </section>
              ))}

              <div className={s.actions}>
                <button
                  type="button"
                  className={clsx(
                    s.primaryButton,
                    !canGoNext && s.primaryButtonDisabled
                  )}
                  onClick={() => {
                    if (!canGoNext) {
                      toast.error(
                        t('oth360Form.submit.answerStatusBeforeProceeding')
                      );
                      return;
                    }
                    setTab('utvikling');
                  }}
                  disabled={hasSubmitted || isSubmitting}
                >
                  {t('oth360Form.buttons.next')}
                </button>
              </div>
            </>
          ) : (
            <>
              <section className={s.textBlock}>
                <p className={s.textBlockTitle}>
                  {t('oth360Form.sections.continueWith')}
                </p>
                <p className={s.textBlockPrompt}>
                  {schema.prompts.continueWith}
                </p>
                <textarea
                  className={s.textarea}
                  value={devText.continueWith}
                  onChange={(e) =>
                    setDevText((p) => ({ ...p, continueWith: e.target.value }))
                  }
                  disabled={hasSubmitted || isSubmitting}
                />
              </section>

              <section className={s.textBlock}>
                <p className={s.textBlockTitle}>
                  {t('oth360Form.sections.stopWith')}
                </p>
                <p className={s.textBlockPrompt}>{schema.prompts.stopWith}</p>
                <textarea
                  className={s.textarea}
                  value={devText.stopWith}
                  onChange={(e) =>
                    setDevText((p) => ({ ...p, stopWith: e.target.value }))
                  }
                  disabled={hasSubmitted || isSubmitting}
                />
              </section>

              <section className={s.textBlock}>
                <p className={s.textBlockTitle}>
                  {t('oth360Form.sections.startWith')}
                </p>
                <p className={s.textBlockPrompt}>{schema.prompts.startWith}</p>
                <textarea
                  className={s.textarea}
                  value={devText.startWith}
                  onChange={(e) =>
                    setDevText((p) => ({ ...p, startWith: e.target.value }))
                  }
                  disabled={hasSubmitted || isSubmitting}
                />
              </section>

              <p className={s.note}>{t('oth360Form.note')}</p>

              <div className={s.actions}>
                <button
                  type="submit"
                  className={clsx(
                    s.primaryButton,
                    !(canSubmit || isSelfLink) && s.primaryButtonDisabled
                  )}
                  onClick={() => {
                    if (!(canSubmit || isSelfLink)) {
                      toast.error(
                        t('oth360Form.submit.fillAllBeforeSubmitting')
                      );
                    }
                  }}
                  disabled={
                    hasSubmitted || isSubmitting || !(canSubmit || isSelfLink)
                  }
                >
                  {t('oth360Form.buttons.submit')}
                </button>
              </div>
            </>
          )}
        </form>
      </main>
    </div>
  );
};
